import { t } from "@travily/i18n";
import { Field, useField } from "@ui/components/Field";
import { ExitIcon } from "@ui/components/Icon";
import { StandardDropZone } from "@ui/components/StandardDropZone";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import type { ReactNode } from "react";

export type Selection = { type: "default" } | { type: "upload" } | { type: "preset"; key: string };

const imagePicker = tv({
  slots: {
    container: "m-0 min-w-0 border-0 p-0",
    item: "relative rounded-xl",
    optionButton: [
      "relative cursor-pointer overflow-clip rounded-xl bg-transparent p-0",
      "outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring",
    ],
  },
  variants: {
    layout: {
      row: {
        container: "flex gap-3",
        item: "min-w-0 flex-1 aspect-square",
        optionButton: "min-w-0 flex-1 aspect-square",
      },
      grid: {
        container: "flex flex-col gap-3 md:grid md:grid-cols-2",
        item: "h-25 md:h-20",
        optionButton: "h-25 md:h-20",
      },
    },
  },
});

function SelectionRing() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 rounded-xl outline-solid outline-4 outline-green-500 -outline-offset-4"
    />
  );
}

export interface ImagePickerProps {
  /** Visible label text and accessible group name. */
  label: string;
  /** Visually hide the label while keeping it accessible (e.g. in a wizard stage where the
   *  heading already names the field). */
  isLabelHidden?: boolean;
  /** "row" for profile pictures (horizontal); "grid" for a 2-col grid that stacks vertically on mobile. */
  layout: "row" | "grid";
  /** Controlled selection state. */
  selection: Selection;
  /** Called when the user changes selection. */
  onSelectionChange: (selection: Selection) => void;
  /** Preview URL for the upload slot (blob URL or existing image URL). */
  uploadPreview: string | null;
  /** Called when the user selects a file via the upload slot. */
  onFileSelect: (file: File) => void;
  /** Called when the user removes their uploaded file. */
  onRemove: () => void;
  /** Rendered as the first option (e.g., default avatar or default cover). */
  defaultOption: ReactNode;
  /** Preset image options displayed after the upload slot. Pass `content` for custom rendering, otherwise `src`.
   *  A `disabled` preset can't be selected and shows `disabledHint` over a dimmed tile. */
  presetImages: readonly {
    key: string;
    alt: string;
    src?: string;
    content?: ReactNode;
    disabled?: boolean;
    disabledHint?: ReactNode;
  }[];
  /** Validation message rendered below the options. */
  errorMessage?: ReactNode;
}

/** Image selection group: default option, file upload slot, and preset image options. Single selection. */
export function ImagePicker(props: ImagePickerProps) {
  const { errorMessage } = props;

  return (
    <Field isInvalid={Boolean(errorMessage)}>
      <Field.Label isHidden={props.isLabelHidden}>{props.label}</Field.Label>
      <ImagePickerOptions {...props} />
      {errorMessage ? <Field.Error>{errorMessage}</Field.Error> : null}
    </Field>
  );
}

function ImagePickerOptions({
  label,
  layout,
  selection,
  onSelectionChange,
  uploadPreview,
  onFileSelect,
  onRemove,
  defaultOption,
  presetImages,
}: ImagePickerProps) {
  const ctx = useField();
  const styles = imagePicker({ layout });
  const isUploadSelected = selection.type === "upload";

  return (
    <fieldset aria-labelledby={ctx?.labelId} className={styles.container()}>
      <div className={styles.item()}>
        <StandardDropZone
          aria-label={`Upload ${label.toLowerCase()}`}
          acceptedFileTypes={["image/png", "image/jpeg", "image/webp"]}
          className="size-full"
          onFileSelect={(file) => {
            onFileSelect(file);
            onSelectionChange({ type: "upload" });
          }}
          previewSrc={uploadPreview ?? undefined}
        />
        {isUploadSelected && uploadPreview && (
          <Button
            aria-label={t("components.image_picker.remove_upload")}
            className="absolute end-2 top-2 z-10 flex size-7 cursor-pointer items-center justify-center rounded-full bg-green-500 text-white outline-none hover:bg-green-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring pressed:bg-green-600"
            onPress={() => {
              onRemove();
              onSelectionChange({ type: "default" });
            }}
          >
            <ExitIcon aria-hidden="true" className="size-4" />
          </Button>
        )}
        {isUploadSelected && <SelectionRing />}
      </div>
      <Button
        aria-label={t("components.image_picker.default_option")}
        aria-pressed={selection.type === "default"}
        className={styles.optionButton()}
        onPress={() => onSelectionChange({ type: "default" })}
      >
        {defaultOption}
        {selection.type === "default" && <SelectionRing />}
      </Button>
      {presetImages.map((image) => {
        const isSelected = selection.type === "preset" && selection.key === image.key;

        return (
          <Button
            key={image.key}
            aria-label={image.alt}
            aria-pressed={isSelected}
            className={styles.optionButton()}
            isDisabled={image.disabled}
            onPress={() => onSelectionChange({ type: "preset", key: image.key })}
          >
            {image.content ?? <img alt="" className="size-full object-cover" src={image.src} />}
            {image.disabled && image.disabledHint ? (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 p-2 text-center text-xs text-neutral-600">
                {image.disabledHint}
              </div>
            ) : null}
            {isSelected && !image.disabled && <SelectionRing />}
          </Button>
        );
      })}
    </fieldset>
  );
}
